import styled from '@emotion/styled';
import React, { ReactElement, useMemo, useState } from 'react';
import { ROUTE_INSTITUTION_ADMIN } from '../../../../ReduxStorage/routeActions';
import type { SubscriptionLinkedInstitutionsTableItem } from '../../../../ReduxStorage/UserSubscriptionPlans/selectors';
import { Link } from '../../../containers/Link';
import { Paginate } from '../../Pagination/Paginate';
import { OL_COLOUR_NAVY_BABY } from '../../Style/colours';

interface InstitutionUsageTableProps {
  institutions: SubscriptionLinkedInstitutionsTableItem[];
}

const Table = styled('table')({
  width: '100%',
  fontSize: 16,
  marginTop: 20,
  marginBottom: 24,
  padding: 4,
  border: 'solid 1px #D8D8D8',
  borderRadius: 4,

  'th, td': {
    padding: 24,
    borderBottom: 'solid 1px #D8D8D8',
  },

  td: {
    textAlign: 'center',
  },

  'tr td:first-child': {
    textAlign: 'left',
    fontWeight: 600,
    textDecoration: 'none',

    'a, a:hover, a:visited': {
      color: `${OL_COLOUR_NAVY_BABY} !important`,
    },
  },

  'tr td:first-child, tr th:first-child': {
    borderRight: 'solid 1px #D8D8D8',
  },

  'tr:last-child td': {
    borderBottom: 'none',
  },
});

export const InstitutionUsageTable = ({
  institutions,
}: InstitutionUsageTableProps): ReactElement => {
  // sort institution table item by its name
  const sortedInstitutions = institutions.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(25);
  const startIndex = useMemo(() => (currentPageIndex - 1) * currentPageSize, [
    currentPageIndex,
    currentPageSize,
  ]);
  const showedInstitutions = sortedInstitutions.slice(
    startIndex,
    startIndex + currentPageSize
  );

  return (
    <div>
      <Table>
        <tbody>
          <tr>
            <th>Institution</th>
            <th>Courses</th>
            <th>Active classes</th>
            <th>Active learners</th>
          </tr>
          {showedInstitutions.map((inst) => (
            <tr>
              <td>
                <Link
                  to={{
                    type: ROUTE_INSTITUTION_ADMIN,
                    query: {
                      institution: inst.path,
                      action: 'institutionUsage',
                    },
                  }}
                >
                  {inst.name}
                </Link>
              </td>
              <td>{inst.courseCount}</td>
              <td>{inst.cohortCount}</td>
              <td>{inst.learnerCount}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div>
        <Paginate
          page={currentPageIndex}
          totalPages={Math.ceil(sortedInstitutions.length / currentPageSize)}
          pageSize={currentPageSize}
          totalResults={sortedInstitutions.length}
          handlePageChange={(newIndex) => setCurrentPageIndex(newIndex)}
          showPageSizeSelector
          handlePageSizeChange={(newPageSize) =>
            setCurrentPageSize(newPageSize)
          }
        />
      </div>
    </div>
  );
};
